"use client";

import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { CheckIcon, ChevronRightIcon, CircleIcon } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * DropdownMenu Component
 *
 * A dropdown menu component for displaying a list of actions or options.
 * Built on top of Radix UI DropdownMenu primitive.
 *
 * @example
 * ```tsx
 * <DropdownMenu>
 *   <DropdownMenuTrigger>Open Menu</DropdownMenuTrigger>
 *   <DropdownMenuContent>
 *     <DropdownMenuItem>Item 1</DropdownMenuItem>
 *     <DropdownMenuItem>Item 2</DropdownMenuItem>
 *   </DropdownMenuContent>
 * </DropdownMenu>
 * ```
 */
function DropdownMenu({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
    return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />;
}
DropdownMenu.displayName = "DropdownMenu";

/**
 * DropdownMenuPortal Component
 *
 * Portal for rendering dropdown menu content.
 */
function DropdownMenuPortal({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Portal>) {
    return <DropdownMenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />;
}
DropdownMenuPortal.displayName = "DropdownMenuPortal";

/**
 * DropdownMenuTrigger Component
 *
 * The trigger element that opens the dropdown menu.
 */
function DropdownMenuTrigger({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
    return <DropdownMenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />;
}
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

/**
 * DropdownMenuContent Component
 *
 * Main content container for the dropdown menu.
 */
function DropdownMenuContent({
    className,
    sideOffset = 4,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
    return (
        <DropdownMenuPrimitive.Portal>
            <DropdownMenuPrimitive.Content
                data-slot="dropdown-menu-content"
                sideOffset={sideOffset}
                className={cn(
                    "setu:bg-popover setu:text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) setu:min-w-[8rem] setu:origin-(--radix-dropdown-menu-content-transform-origin) setu:overflow-x-hidden setu:overflow-y-auto setu:rounded-md setu:!border setu:p-1 setu:shadow-md",
                    className
                )}
                {...props}
            />
        </DropdownMenuPrimitive.Portal>
    );
}
DropdownMenuContent.displayName = "DropdownMenuContent";

/**
 * DropdownMenuGroup Component
 *
 * Groups related dropdown menu items together.
 */
function DropdownMenuGroup({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Group>) {
    return <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />;
}
DropdownMenuGroup.displayName = "DropdownMenuGroup";

/**
 * DropdownMenuItem Component
 *
 * Individual item in the dropdown menu.
 */
function DropdownMenuItem({
    className,
    inset,
    variant = "default",
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
    variant?: "default" | "destructive";
}) {
    return (
        <DropdownMenuPrimitive.Item
            data-slot="dropdown-menu-item"
            data-inset={inset}
            data-variant={variant}
            className={cn(
                "setu:focus:bg-accent setu:focus:text-accent-foreground setu:data-[variant=destructive]:text-destructive setu:data-[variant=destructive]:focus:bg-destructive/10 setu:dark:data-[variant=destructive]:focus:bg-destructive/20 setu:data-[variant=destructive]:focus:text-destructive setu:data-[variant=destructive]:*:[svg]:!text-destructive setu:[&_svg:not([class*='text-'])]:text-muted-foreground setu:relative setu:flex setu:cursor-default setu:items-center setu:gap-2 setu:rounded-sm setu:px-2 setu:py-1.5 setu:text-sm setu:outline-hidden setu:select-none setu:data-[disabled]:pointer-events-none setu:data-[disabled]:opacity-50 setu:data-[inset]:pl-8 [&_svg]:pointer-events-none setu:[&_svg]:shrink-0 setu:[&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        />
    );
}
DropdownMenuItem.displayName = "DropdownMenuItem";

/**
 * DropdownMenuCheckboxItem Component
 *
 * Checkbox item in the dropdown menu.
 */
function DropdownMenuCheckboxItem({
    className,
    children,
    checked,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
    return (
        <DropdownMenuPrimitive.CheckboxItem
            data-slot="dropdown-menu-checkbox-item"
            className={cn(
                "focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                className
            )}
            checked={checked}
            {...props}
        >
            <span className="setu:pointer-events-none setu:absolute setu:left-2 setu:flex setu:size-3.5 setu:items-center setu:justify-center">
                <DropdownMenuPrimitive.ItemIndicator>
                    <CheckIcon className="size-4" />
                </DropdownMenuPrimitive.ItemIndicator>
            </span>
            {children}
        </DropdownMenuPrimitive.CheckboxItem>
    );
}
DropdownMenuCheckboxItem.displayName = "DropdownMenuCheckboxItem";

/**
 * DropdownMenuRadioGroup Component
 *
 * Radio group for mutually exclusive options in the dropdown menu.
 */
function DropdownMenuRadioGroup({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>) {
    return <DropdownMenuPrimitive.RadioGroup data-slot="dropdown-menu-radio-group" {...props} />;
}
DropdownMenuRadioGroup.displayName = "DropdownMenuRadioGroup";

/**
 * DropdownMenuRadioItem Component
 *
 * Radio item in the dropdown menu.
 */
function DropdownMenuRadioItem({
    className,
    children,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) {
    return (
        <DropdownMenuPrimitive.RadioItem
            data-slot="dropdown-menu-radio-item"
            className={cn(
                "setu:focus:bg-accent setu:focus:text-accent-foreground setu:relative setu:flex setu:cursor-default setu:items-center setu:gap-2 setu:rounded-sm setu:py-1.5 setu:pr-2 setu:pl-8 setu:text-sm setu:outline-hidden setu:select-none setu:data-[disabled]:pointer-events-none setu:data-[disabled]:opacity-50 setu:[&_svg]:pointer-events-none setu:[&_svg]:shrink-0 setu:[&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        >
            <span className="setu:pointer-events-none setu:absolute setu:left-2 setu:flex setu:size-3.5 setu:items-center setu:justify-center">
                <DropdownMenuPrimitive.ItemIndicator>
                    <CircleIcon className="setu:size-2 setu:fill-current" />
                </DropdownMenuPrimitive.ItemIndicator>
            </span>
            {children}
        </DropdownMenuPrimitive.RadioItem>
    );
}
DropdownMenuRadioItem.displayName = "DropdownMenuRadioItem";

/**
 * DropdownMenuLabel Component
 *
 * Label for a section of the dropdown menu.
 */
function DropdownMenuLabel({
    className,
    inset,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
}) {
    return (
        <DropdownMenuPrimitive.Label
            data-slot="dropdown-menu-label"
            data-inset={inset}
            className={cn("setu:px-2 setu:py-1.5 setu:text-sm setu:font-medium setu:data-[inset]:pl-8", className)}
            {...props}
        />
    );
}
DropdownMenuLabel.displayName = "DropdownMenuLabel";

/**
 * DropdownMenuSeparator Component
 *
 * Visual separator between dropdown menu items.
 */
function DropdownMenuSeparator({ className, ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
    return (
        <DropdownMenuPrimitive.Separator
            data-slot="dropdown-menu-separator"
            className={cn("setu:!bg-border setu:-mx-1 setu:my-1 setu:h-px", className)}
            {...props}
        />
    );
}
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

/**
 * DropdownMenuShortcut Component
 *
 * Keyboard shortcut display for dropdown menu items.
 */
function DropdownMenuShortcut({ className, ...props }: React.ComponentProps<"span">) {
    return (
        <span
            data-slot="dropdown-menu-shortcut"
            className={cn("setu:text-muted-foreground setu:ml-auto setu:text-xs setu:tracking-widest", className)}
            {...props}
        />
    );
}
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

/**
 * DropdownMenuSub Component
 *
 * Sub-menu container for nested dropdown menus.
 */
function DropdownMenuSub({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>) {
    return <DropdownMenuPrimitive.Sub data-slot="dropdown-menu-sub" {...props} />;
}
DropdownMenuSub.displayName = "DropdownMenuSub";

/**
 * DropdownMenuSubTrigger Component
 *
 * Trigger for opening a sub-menu.
 */
function DropdownMenuSubTrigger({
    className,
    inset,
    children,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
}) {
    return (
        <DropdownMenuPrimitive.SubTrigger
            data-slot="dropdown-menu-sub-trigger"
            data-inset={inset}
            className={cn(
                "setu:focus:bg-accent setu:focus:text-accent-foreground setu:data-[state=open]:bg-accent setu:data-[state=open]:text-accent-foreground setu:flex setu:cursor-default setu:items-center setu:rounded-sm setu:px-2 setu:py-1.5 setu:text-sm setu:outline-hidden setu:select-none setu:data-[inset]:pl-8",
                className
            )}
            {...props}
        >
            {children}
            <ChevronRightIcon className="ml-auto size-4" />
        </DropdownMenuPrimitive.SubTrigger>
    );
}
DropdownMenuSubTrigger.displayName = "DropdownMenuSubTrigger";

/**
 * DropdownMenuSubContent Component
 *
 * Content container for sub-menus.
 */
function DropdownMenuSubContent({
    className,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
    return (
        <DropdownMenuPrimitive.SubContent
            data-slot="dropdown-menu-sub-content"
            className={cn(
                "setu:bg-popover setu:text-popover-foreground setu:data-[state=open]:animate-in setu:data-[state=closed]:animate-out setu:data-[state=closed]:fade-out-0 setu:data-[state=open]:fade-in-0 setu:data-[state=closed]:zoom-out-95 setu:data-[state=open]:zoom-in-95 setu:data-[side=bottom]:slide-in-from-top-2 setu:data-[side=left]:slide-in-from-right-2 setu:data-[side=right]:slide-in-from-left-2 setu:data-[side=top]:slide-in-from-bottom-2 z-50 setu:min-w-[8rem] setu:origin-(--radix-dropdown-menu-content-transform-origin) setu:overflow-hidden setu:rounded-md setu:!border setu:p-1 setu:shadow-lg",
                className
            )}
            {...props}
        />
    );
}
DropdownMenuSubContent.displayName = "DropdownMenuSubContent";

export {
    DropdownMenu,
    DropdownMenuPortal,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuLabel,
    DropdownMenuItem,
    DropdownMenuCheckboxItem,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuSubContent,
};
